import "./Select.scss";
import { useState, SelectHTMLAttributes, useEffect } from "react";

type option = {
  label: string;
  value: string | number;
  disabled?: boolean;
  default?: boolean;
};

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  type?: "default" | "labeled";
  options: option[];
  label?: string;
  required?: boolean;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  placeholder?: string;
  value?: string | number;
  isInvalid?: boolean;
  errorMessage?: string;
  resetOnOptionsChange?: boolean;
}

export const Select = ({
  type,
  options,
  label,
  required,
  onChange,
  placeholder = " ",
  value,
  isInvalid = false,
  errorMessage,
  resetOnOptionsChange = true,
  ...rest
}: SelectProps) => {
  type = type ?? "default";

  const [internalValue, setInternalValue] = useState<string | number>("");
  const [isFocused, setIsFocused] = useState(false);
  const [previousOptions, setPreviousOptions] = useState<option[]>(options);

  const currentValue = value !== undefined ? value : internalValue;
  const isEmpty = currentValue === "";

  useEffect(() => {
    if (resetOnOptionsChange && JSON.stringify(previousOptions) !== JSON.stringify(options)) {
      const currentValueExists = options.some(option => option.value === currentValue);
      
      if (!currentValueExists) {
        if (value === undefined) {
          setInternalValue("");
        }
        const syntheticEvent = {
          target: { value: "" }
        } as React.ChangeEvent<HTMLSelectElement>;
        onChange(syntheticEvent);
      }
      
      setPreviousOptions(options);
    }
  }, [options, currentValue, resetOnOptionsChange, onChange, value, previousOptions]);

  useEffect(() => {
    if (value === undefined && options.length > 0) {
      const defaultOption = options.find((option) => option.default);
      if (defaultOption && internalValue === "") {
        setInternalValue(defaultOption.value);

        const syntheticEvent = {
          target: { value: defaultOption.value }
        } as React.ChangeEvent<HTMLSelectElement>;
        onChange(syntheticEvent);
      }
    }
  }, [options, value]);

  const selectOptions = [
    { value: "", label: placeholder, disabled: true },
    ...options,
  ];

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = e.target.value;

    if (value === undefined) {
      setInternalValue(newValue);
    }

    onChange(e);
  };

  const handleFocus = () => setIsFocused(true);
  const handleBlur = () => setIsFocused(false);

  const containerClass =
    type === "labeled"
    ? `input-select ${
        isEmpty && !isFocused ? "input-select--empty" : "input-select--filled"
      } ${isInvalid ? 'input-select--invalid' : ''}`
    : `input-select ${isInvalid ? 'input-select--invalid' : ''}`;

  return type == "default" ? (
    <span className="input-select">
      <select
        value={currentValue}
        className="input-select__select"
        onChange={handleChange}
        {...rest}
      >
        {selectOptions.map((option, key) => (
          <option key={key} value={option.value} disabled={option.disabled}>
            {option.label}
          </option>
        ))}
      </select>
    </span>
  ) : (
    <span className={containerClass}>
      <label
        className={
          required
            ? "input-select__label input-select__label--required"
            : "input-select__label"
        }
      >
        {label}
      </label>
      <select
        value={currentValue}
        className="input-select__select input-select__select--options"
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        {...rest}
      >
        {selectOptions.map((option, key) => (
          <option key={key} value={option.value} disabled={option.disabled}>
            {option.label}
          </option>
        ))}
      </select>
      {isInvalid && errorMessage && (
        <div className="input-select__error">{errorMessage}</div>
      )}
    </span>
  );
};

export default Select